import {
  Task,
  Update,
  defModule,
  type TaskDeclaration,
  type TaskMessage,
  type UpdateResult
} from '@st/redux'
import type { BillingState } from '@st/sdk'
import { Effect, pipe } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import { Routing, SdkClient } from '~/effects'
import { observeOrganizationRoute, runSdkRequest } from '~/features/module-runtime'

type BillingInputs = {
  currentUserId: string | null
  organizationSlug: string | null
}

type ActiveBillingInputs = {
  currentUserId: string
  organizationSlug: string
}

type BillingSession = ActiveBillingInputs & {
  organizationId: string
}

type CheckoutState =
  | { status: 'idle' }
  | { status: 'generating'; quantity: number; continueUrl: string }
  | { status: 'failed' }

type IdleBillingState = BillingInputs & { status: 'idle' }
type LoadingOrganizationState = ActiveBillingInputs & { status: 'loadingOrganization' }
type LoadingBillingState = BillingSession & { status: 'loadingBilling' }
type BillingLoadFailedState = BillingSession & { status: 'loadFailed' }
type LoadedBillingState = BillingSession & {
  status: 'loaded'
  billingState: BillingState
  checkout: CheckoutState
}

type NextBillingState = IdleBillingState | LoadingOrganizationState | LoadingBillingState

type FinishingCheckoutState = {
  status: 'finishingCheckout'
  next: NextBillingState
}

export type OrganizationBillingState =
  | IdleBillingState
  | LoadingOrganizationState
  | LoadingBillingState
  | BillingLoadFailedState
  | LoadedBillingState
  | FinishingCheckoutState

const observeBillingOrganization = observeOrganizationRoute({
  name: P.union(
    'logged_in_pricing',
    'organization_balance',
    'folder_list',
    'folder_list_send_client_organizers',
    'folder_list_organizer_reminders'
  )
})

const loadBillingState = Task.define((_currentUserId: string, organizationId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const billingState = yield* runSdkRequest(
      sdk.send({ type: 'billing/getBillingState', organizationId })
    )
    if (!billingState.ok) return { type: 'billingLoadFailed' } as const
    return { type: 'billingLoaded', billingState: billingState.response } as const
  })
)

type OrganizationBillingMessage =
  | TaskMessage<typeof observeBillingOrganization>
  | TaskMessage<typeof loadBillingState>
  | { type: 'startPurchaseCreditsCheckoutFlow'; quantity: number; continueUrl: string }
  | { type: 'retryBillingLoad' }
  | { type: 'checkoutLinkGenerationCompleted' }
  | { type: 'checkoutLinkGenerationFailed' }

export const LOW_BALANCE_BANNER_THRESHOLD = -10
export const LOW_BALANCE_HARD_BLOCK_THRESHOLD = -25

export type LowBalanceModel = {
  currentBalance: number
  hardBlock: boolean
}

export function selLowBalanceModel(state: OrganizationBillingState): LowBalanceModel | undefined {
  if (state.status != 'loaded') return undefined

  const currentBalance = state.billingState.creditsStatement.currentBalance
  if (currentBalance > LOW_BALANCE_BANNER_THRESHOLD) return undefined

  return {
    currentBalance,
    hardBlock: currentBalance <= LOW_BALANCE_HARD_BLOCK_THRESHOLD
  }
}

/**
 * Billing activates only when both a billing-bearing route and an authenticated
 * user exist. Organization and billing reads are state-owned Tasks, so replacing
 * their phase revokes obsolete output. Checkout is different: once accepted it
 * is a Command that may have created remote state and must redirect even if the
 * route changes. `finishingCheckout` reserves that one accepted operation until
 * its domain outcome arrives; no request identity enters the message protocol.
 * Shared organization failures remain presentation owned by the outer context.
 */
export const organizationBillingModule = defModule<{
  state: OrganizationBillingState
  message: OrganizationBillingMessage
}>({
  init: () => ({ status: 'idle', organizationSlug: null, currentUserId: null }),
  tasks: organizationBillingTasks,
  update: pipe(
    updateOrganizationBilling,
    Update.ignoreWhen<OrganizationBillingState, OrganizationBillingMessage>(
      isMatching({
        state: { status: 'loaded', checkout: { status: 'generating' } },
        message: { type: 'startPurchaseCreditsCheckoutFlow' }
      })
    )
  )
})

function organizationBillingTasks(
  state: OrganizationBillingState
): TaskDeclaration<OrganizationBillingMessage>[] {
  const observations: TaskDeclaration<OrganizationBillingMessage>[] = [observeBillingOrganization()]

  return match(state)
    .returnType<TaskDeclaration<OrganizationBillingMessage>[]>()
    .with({ status: 'loadingOrganization' }, () => observations)
    .with({ status: 'loadingBilling' }, ({ currentUserId, organizationId }) => [
      ...observations,
      loadBillingState(currentUserId, organizationId)
    ])
    .with(
      { status: P.union('idle', 'loadFailed', 'loaded', 'finishingCheckout') },
      () => observations
    )
    .exhaustive()
}

function updateOrganizationBilling(
  state: OrganizationBillingState,
  message: OrganizationBillingMessage
): UpdateResult<OrganizationBillingState, OrganizationBillingMessage> {
  return match({ state, message })
    .returnType<UpdateResult<OrganizationBillingState, OrganizationBillingMessage>>()
    .with({ message: { type: 'organizationRouteChanged' } }, ({ state, message }) =>
      reconcileBillingContext(state, message.context)
    )
    .with(
      { state: { status: 'loadingBilling' }, message: { type: 'billingLoaded' } },
      ({ state, message }) => ({
        ...state,
        status: 'loaded',
        billingState: message.billingState,
        checkout: { status: 'idle' }
      })
    )
    .with(
      {
        state: { status: 'loadingBilling' },
        message: { type: 'billingLoadFailed' }
      },
      ({ state }) => ({
        ...state,
        status: 'loadFailed'
      })
    )
    .with(
      {
        state: { status: 'loadFailed' },
        message: { type: 'retryBillingLoad' }
      },
      ({ state }) => ({
        ...state,
        status: 'loadingBilling'
      })
    )
    .with(
      {
        state: { status: 'loaded', checkout: { status: P.union('idle', 'failed') } },
        message: { type: 'startPurchaseCreditsCheckoutFlow' }
      },
      ({ state, message }) => [
        {
          ...state,
          checkout: {
            status: 'generating',
            quantity: message.quantity,
            continueUrl: message.continueUrl
          }
        },
        generateCheckoutLink(state.organizationId, message.quantity, message.continueUrl)
      ]
    )
    .with(
      {
        state: { status: 'loaded', checkout: { status: 'generating' } },
        message: { type: 'checkoutLinkGenerationCompleted' }
      },
      ({ state }) => ({ ...state, checkout: { status: 'idle' } })
    )
    .with(
      {
        state: { status: 'loaded', checkout: { status: 'generating' } },
        message: { type: 'checkoutLinkGenerationFailed' }
      },
      ({ state }) => ({ ...state, checkout: { status: 'failed' } })
    )
    .with(
      {
        state: { status: 'finishingCheckout' },
        message: P.union(
          { type: 'checkoutLinkGenerationCompleted' },
          { type: 'checkoutLinkGenerationFailed' }
        )
      },
      ({ state }) => state.next
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function reconcileBillingContext(
  state: OrganizationBillingState,
  context: TaskMessage<typeof observeBillingOrganization>['context']
): OrganizationBillingState {
  const next = billingStateForContext(context)

  if (state.status == 'finishingCheckout') {
    return { ...state, next }
  }

  if (state.status == 'loaded' && state.checkout.status == 'generating') {
    if (sameBillingSession(state, next)) return state
    return { status: 'finishingCheckout', next }
  }

  if (sameBillingSession(state, next)) return state
  return next
}

function billingStateForContext(
  context: TaskMessage<typeof observeBillingOrganization>['context']
): NextBillingState {
  if (context.status == 'inactive') {
    return { status: 'idle', currentUserId: null, organizationSlug: null }
  }

  const organizationSlug = context.route.organizationSlug
  if (!context.currentUserId) {
    return { status: 'idle', currentUserId: null, organizationSlug }
  }

  if (
    context.status == 'loading' ||
    context.status == 'loadFailed' ||
    context.status == 'unavailable'
  ) {
    return {
      status: 'loadingOrganization',
      currentUserId: context.currentUserId,
      organizationSlug
    }
  }

  return {
    status: 'loadingBilling',
    currentUserId: context.currentUserId,
    organizationSlug,
    organizationId: context.organization.id
  }
}

function sameBillingSession(
  state: Exclude<OrganizationBillingState, FinishingCheckoutState>,
  next: NextBillingState
): boolean {
  return (
    next.status == 'loadingBilling' &&
    'organizationId' in state &&
    state.currentUserId == next.currentUserId &&
    state.organizationSlug == next.organizationSlug &&
    state.organizationId == next.organizationId
  )
}

function generateCheckoutLink(
  organizationId: string,
  quantity: number,
  continueUrl: string
): Effect.Effect<OrganizationBillingMessage, never, SdkClient | Routing> {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({
        type: 'billing/generatePurchaseCreditsCheckoutLink',
        organizationId,
        quantity,
        continueUrl
      })
    )
    if (!result.ok) return { type: 'checkoutLinkGenerationFailed' } as const

    if (!result.response.checkoutSessionUrl) {
      throw new Error('Checkout link response is missing checkoutSessionUrl')
    }

    const routing = yield* Routing
    yield* routing.redirectTo(result.response.checkoutSessionUrl)
    return { type: 'checkoutLinkGenerationCompleted' } as const
  })
}

function unexpectedMessage(
  state: OrganizationBillingState,
  message: OrganizationBillingMessage
): never {
  throw new Error(`Unexpected organization billing message ${message.type} while ${state.status}`)
}
